import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mic2, Pause, Play, Plus, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { absUrl, errMsg, previewVoice } from '../../lib/aiStudioApi';
import { OwnVoiceDialog } from './OwnVoiceDialog';
import { cn } from '../../lib/utils';

export function VoiceSelect({ voices, value, onChange, testId, placeholder = 'Auto voice' }) {
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1.5 text-xs text-neutral-200">
      <option value="">{placeholder}</option>
      <optgroup label="Konnect voice library">
        {voices.stock.map((v) => <option key={v.id} value={v.id}>{v.name} — {v.gender}, {v.style}</option>)}
      </optgroup>
      {voices.custom.length > 0 && (
        <optgroup label="My own voices">
          {voices.custom.map((v) => <option key={v.id} value={v.id} disabled={!v.ready}>{v.name}{v.ready ? '' : ' (training…)'}</option>)}
        </optgroup>
      )}
    </select>
  );
}

function Equalizer() {
  return (
    <span className="konnect-eq inline-flex h-3 items-end gap-[2px]">
      {[0, 1, 2, 3].map((i) => <span key={i} className="w-[3px] rounded-sm bg-amber-300" style={{ height: '100%', animationDelay: `${i * 0.15}s` }} />)}
    </span>
  );
}

export function VoiceLibrary({ project, voices, onVoicesChanged, onNarrator }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState('');
  const [loading, setLoading] = useState('');
  const [ownOpen, setOwnOpen] = useState(false);

  const playUrl = (url, id) => {
    const a = audioRef.current;
    a.src = url;
    a.play().then(() => setPlaying(id)).catch(() => toast.error('Could not play this sample'));
  };

  async function play(v, custom) {
    if (playing === v.id) { audioRef.current.pause(); setPlaying(''); return; }
    if (custom || v.preview_url) { playUrl(absUrl(v.preview_url), v.id); return; }
    setLoading(v.id);
    try {
      const r = await previewVoice(v.id);
      playUrl(absUrl(r.preview_url), v.id);
      onVoicesChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(''); }
  }

  const PlayBtn = ({ v, custom }) => (
    <Button size="sm" variant="outline" onClick={() => play(v, custom)} disabled={loading === v.id || (custom && !v.preview_url)}
      className="h-8 border-neutral-700 bg-neutral-900 text-neutral-200 hover:border-amber-400/60" data-testid={`voice-play-${v.id.replace(':', '-')}`}>
      {loading === v.id ? <Loader2 size={13} className="animate-spin" /> : playing === v.id ? <Pause size={13} /> : <Play size={13} />}
      <span className="ml-1">{playing === v.id ? 'Stop' : 'Preview'}</span>
    </Button>
  );

  return (
    <div className="space-y-5" data-testid="voice-library">
      <audio ref={audioRef} onEnded={() => setPlaying('')} className="hidden" />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-neutral-100">Voice library</div>
          <p className="text-xs text-neutral-500">Each voice's sample is generated once (3 cr), then previews are free for everyone. Cast voices on characters or set the narrator.</p>
        </div>
        <Button onClick={() => setOwnOpen(true)} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="voice-add-own-btn">
          <Mic2 size={14} className="mr-1" /> Use my own voice
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {voices.stock.map((v) => {
          const isNarrator = project.narrator_voice === v.id;
          return (
            <div key={v.id} data-testid={`voice-card-${v.id}`}
              className={cn('rounded-xl border p-3 transition-colors', isNarrator ? 'border-amber-400/60 bg-amber-500/5' : 'border-neutral-800 bg-neutral-950/60')}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-neutral-100">
                    {v.name} {playing === v.id && <Equalizer />}
                  </div>
                  <div className="text-[11px] text-neutral-500">{v.gender} · {v.style}</div>
                </div>
                <Badge className={cn('text-[10px]', v.preview_ready ? 'bg-emerald-500/15 text-emerald-200' : 'bg-neutral-800 text-neutral-400')}>
                  {v.preview_ready ? 'Free preview' : '1st play · 3 cr'}
                </Badge>
              </div>
              <div className="mt-2 text-[11px] text-neutral-400">Best for: {v.best_for}</div>
              <div className="mt-3 flex items-center gap-2">
                <PlayBtn v={v} />
                <Button size="sm" variant="ghost" onClick={() => onNarrator(isNarrator ? '' : v.id)}
                  className={cn('h-8 text-xs', isNarrator ? 'text-amber-300' : 'text-neutral-400')} data-testid={`voice-narrator-${v.id}`}>
                  <Star size={13} className={cn('mr-1', isNarrator && 'fill-amber-300')} /> {isNarrator ? 'Narrator' : 'Set as narrator'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">My own voices</div>
        {voices.custom.length === 0 ? (
          <button type="button" onClick={() => setOwnOpen(true)} data-testid="voice-own-empty"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-800 p-5 text-xs text-neutral-500 hover:border-amber-400/50 hover:text-neutral-300">
            <Plus size={14} /> Record or upload 30 seconds of your voice to clone it
          </button>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {voices.custom.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-2 rounded-lg border border-neutral-800 bg-neutral-950/60 p-3" data-testid={`voice-custom-${v.id.replace(':', '-')}`}>
                <div>
                  <div className="text-sm text-neutral-100">{v.name}</div>
                  <div className="text-[11px] text-neutral-500">{v.language || '—'} · {v.ready ? 'Ready to cast' : 'Training…'}</div>
                </div>
                <div className="flex items-center gap-2">
                  <PlayBtn v={v} custom />
                  <Button size="sm" variant="ghost" disabled={!v.ready} onClick={() => onNarrator(v.id)}
                    className="h-8 text-xs text-neutral-400" data-testid={`voice-custom-narrator-${v.id.replace(':', '-')}`}>
                    <Star size={13} className="mr-1" /> Narrator
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <OwnVoiceDialog open={ownOpen} onOpenChange={setOwnOpen} onCreated={onVoicesChanged} />
    </div>
  );
}
